import { useEffect, useRef } from 'react';

/** Dompurify Packages. */
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

/** Material UI Packages. */
import { Box } from '@mui/material';

/** Available custom colors. */
const COLORS = [
    '#000000',
    '#e60000',
    '#ff9900',
    '#ffff00',
    '#008a00',
    '#0066cc',
    '#9933ff',
    '#ffffff',
    '#facccc',
    '#ffebcc',
    '#ffffcc',
    '#cce8cc',
    '#cce0f5',
    '#ebd6ff',
    '#bbbbbb',
    '#f06666',
    '#ffc266',
    '#ffff66',
    '#66b966',
    '#66a3e0',
    '#c285ff',
    '#888888',
    '#a10000',
    '#b26b00',
    '#b2b200',
    '#006100',
    '#0047b2',
    '#6b24b2',
    '#444444',
    '#5c0000',
    '#663d00',
    '#666600',
    '#003700',
    '#002966',
    '#3d1466',
];

const QuillEditor = ({ defaultValue, onChange, maxLength }) => {
    /** Reference to the DOM element that will contain the Quill editor. */
    const editorRef = useRef(null);
    /** Reference to the Quill editor instance. */
    const quillRef = useRef(null);
    const isDefaultValueApplied = useRef(false);

    useEffect(() => {
        if (editorRef.current && !quillRef.current) {
            /** Text editor toolbar format configuration. */
            quillRef.current = new Quill(editorRef.current, {
                theme: 'snow',
                placeholder: 'Please enter the content...',
                modules: {
                    toolbar: [
                        [{ header: [1, 2, 3, 4, 5, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ color: COLORS }, { background: COLORS }],
                        ['blockquote', 'link'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['code', 'code-block'],
                        ['clean'],
                    ],
                },
            });

            /** Register text change function in quill. */
            quillRef.current.on('text-change', (delta, oldDelta, source) => {
                const text = quillRef.current.getText();
                /** When the maximum length is exceeded, one character is automatically removed and an alert is shown. */
                if (text.length > maxLength + 1) {
                    alert('You have exceeded the maximum number of characters.');
                    quillRef.current.deleteText(maxLength, text.length);
                } else {
                    const html = editorRef.current.querySelector('.ql-editor')?.innerHTML;
                    onChange?.(html);
                }
            });
        }
    }, []);

    /** Load existing content when editing. */
    useEffect(() => {
        if (quillRef.current && defaultValue && !isDefaultValueApplied.current) {
            quillRef.current.clipboard.dangerouslyPasteHTML(defaultValue);
            isDefaultValueApplied.current = true;
        }
    }, [defaultValue]);

    return <Box ref={editorRef} />;
};

export default QuillEditor;
